"use client";
import React from "react";
import { useTranslation } from "react-i18next";
import { ensureUploadUrl, looksImageLike } from "../../../utils/uploads";
import { axiosGateway } from "../../../api/axios";
import "./ChatMessage.css";

const normalize = (v) => (v ? String(v).trim() : "");

function toAxiosGatewayPath(originalUrlOrPath) {
  if (!originalUrlOrPath) return null;

  if (/^(blob:|data:)/i.test(String(originalUrlOrPath).trim())) {
    return null;
  }

  const base = String(axiosGateway?.defaults?.baseURL || "").replace(/\/+$/, "");
  const baseHasApi = /\/api$/i.test(base);

  let pathname = "";
  let search = "";

  if (/^https?:\/\//i.test(originalUrlOrPath)) {
    try {
      const u = new URL(originalUrlOrPath);
      pathname = u.pathname || "";
      search = u.search || "";
    } catch {
      return null;
    }
  } else {
    const s = String(originalUrlOrPath).trim();
    const [p, q] = s.split("?");
    pathname = p || "";
    search = q ? `?${q}` : "";
  }

  if (!pathname.startsWith("/")) pathname = "/" + pathname;
  pathname = pathname.replace(/^\/api\/api\//i, "/api/");

  if (baseHasApi) {
    pathname = pathname.replace(/^\/api(?=\/)/i, "");
    if (!pathname) pathname = "/";
  } else {
    if (!/^\/api(\/|$)/i.test(pathname)) pathname = `/api${pathname}`;
  }

  return `${pathname}${search}`;
}

function looksImageByUrl(url = "") {
  const clean = String(url).split("?")[0].split("#")[0].toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg|ico|avif)(\.enc)?$/.test(clean);
}

function looksVideoByUrl(url = "") {
  const clean = String(url).split("?")[0].split("#")[0].toLowerCase();
  return /\.(mp4|webm|ogg|mov|mkv)(\.enc)?$/.test(clean);
}

/* ─────────────────────────────────────────────────────────────
   SharedDocCard — same visual as ChatInput attachments:
   • image  → thumbnail (chat-attachment-image)
   • pdf/file → icon bubble only, no filename text
   ───────────────────────────────────────────────────────────── */
function SharedDocCard({ doc, mine }) {
  const isImage =
    (doc.type && doc.type.startsWith("image")) ||
    looksImageByUrl(doc.pdfUrl || "");

  if (isImage && doc.pdfUrl) {
    return (
      <a
        href={doc.pdfUrl}
        target="_blank"
        rel="noreferrer"
        className="chat-attachment-item"
        style={{ cursor: "pointer", textDecoration: "none" }}
      >
        <img src={doc.pdfUrl} alt="" className="chat-attachment-image" />
      </a>
    );
  }

  const isPdf = (doc.type || doc.mimeType || "").toLowerCase().includes("pdf");
  const pdfLocation = doc.pdfUrl || doc.url || doc.fileUrl || "";
  const displayName = doc.title || doc.fileName || doc.name || "Document PDF";
  const label = isPdf ? displayName : displayName;
  const subtitle = isPdf ? (doc.description || "PDF document") : (doc.type || doc.mimeType || "Fichier");

  const href = pdfLocation || null;

  return (
    <a
      href={href || "#"}
      target={href ? "_blank" : undefined}
      rel={href ? "noreferrer" : undefined}
      className={`chat-attachment-card ${mine ? "chat-attachment-card--sent" : "chat-attachment-card--received"}`}
      style={{ textDecoration: 'none', cursor: href ? 'pointer' : 'default' }}
      onClick={(e) => {
        if (!href) {
          e.preventDefault();
        }
      }}
    >
      <div className="chat-attachment-card-icon">
        <FileIcon />
      </div>
      <div className="chat-attachment-card-info">
        <div className="chat-attachment-card-name" style={{ wordBreak: "break-all" }}>{label}</div>
        {isPdf && pdfLocation && (
          <span className="chat-attachment-card-download" style={{ fontSize: '12px', color: '#1d4ed8', textDecoration: 'underline' }}>
            {t("messaging.chatMessage.downloadPdf", "Télécharger le PDF")}
          </span>
        )}
        {isPdf && pdfLocation && (
          <div className="chat-attachment-card-link" style={{ fontSize: '11px', color: '#2563eb', wordBreak: 'break-all' }}>
            {pdfLocation}
          </div>
        )}
        <div className="chat-attachment-card-subtitle">{subtitle}</div>
      </div>
    </a>
  );
}

export default function ChatMessage({ msg, me }) {
  const { t } = useTranslation();
  if (!msg) return null;

  const meId = normalize(me);
  const sender = normalize(msg.fromUserId);
  const mine = sender === meId;

  const time = msg.createdAt ? new Date(msg.createdAt) : new Date();
  const hh = String(time.getHours()).padStart(2, "0");
  const mm = String(time.getMinutes()).padStart(2, "0");
  const timeStr = `${hh}:${mm}`;

  const originalUrl = msg.fileUrl ? ensureUploadUrl(msg.fileUrl) : null;
  const mime = (msg.mimeType || "").toLowerCase();
  const fileNameLower = (msg.fileName || "").toLowerCase();

  const isImage =
    !!originalUrl &&
    (looksImageLike({ ...msg, fileUrl: originalUrl }) ||
      looksImageByUrl(originalUrl) ||
      mime.startsWith("image/") ||
      /\.(png|jpe?g|gif|webp|bmp|svg|ico|avif)$/i.test(fileNameLower));

  const isVideo =
    !!originalUrl &&
    (mime.startsWith("video/") ||
      looksVideoByUrl(originalUrl) ||
      /\.(mp4|webm|ogg|mov|mkv)$/i.test(fileNameLower));

  const axiosPath = React.useMemo(() => {
    if (!originalUrl) return null;
    return toAxiosGatewayPath(originalUrl);
  }, [originalUrl]);

  const needsBlob = (isImage || isVideo) && !!axiosPath;

  const [blobUrl, setBlobUrl] = React.useState(null);
  const [loadingMedia, setLoadingMedia] = React.useState(false);
  const [mediaError, setMediaError] = React.useState(false);
  const [debugInfo, setDebugInfo] = React.useState(null);

  React.useEffect(() => {
    if (!needsBlob) return;

    let cancelled = false;
    let objectUrl = null;

    setLoadingMedia(true);
    setMediaError(false);
    setBlobUrl(null);

    (async () => {
      try {
        const r = await axiosGateway.get(axiosPath, {
          responseType: "blob",
          withCredentials: true,
        });

        const blob = r?.data;
        const info = {
          axiosPath,
          status: r?.status,
          blobType: blob?.type,
          blobSize: blob?.size,
          isImage,
          isVideo,
        };
        if (!cancelled) setDebugInfo(info);

        const type = String(blob?.type || "").toLowerCase();
        const okType =
          (isImage && type.startsWith("image/")) ||
          (isVideo && type.startsWith("video/")) ||
          type === "application/octet-stream";

        if (!okType || !blob || !blob.size) {
          try {
            const txt = await blob?.text?.();
            console.warn("[MEDIA_NOT_MEDIA]", String(txt || "").slice(0, 200));
          } catch {}
          throw new Error(`Invalid blob (type=${type}, size=${blob?.size})`);
        }

        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setBlobUrl(objectUrl);
          setLoadingMedia(false);
          setMediaError(false);
        }
      } catch (e) {
        console.warn("[MEDIA_FETCH_ERROR]", e);
        if (!cancelled) {
          setLoadingMedia(false);
          setMediaError(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [needsBlob, axiosPath, isImage, isVideo]);

  const displayUrl = needsBlob ? blobUrl : originalUrl;

  /* ── Shared documents (metadata.documents from ShareModal) ── */
  const sharedDocuments = Array.isArray(msg?.metadata?.documents)
    ? msg.metadata.documents
    : [];
  const isDocumentShareMessage =
    sharedDocuments.length > 0 && msg?.metadata?.type === "document_share";

  return (
    <div className={`chat-row ${mine ? "chat-row--sent" : "chat-row--received"}`}>
      <div className="chat-message-group">

        {/* ─── SHARED DOCUMENTS: same look as ChatInput attachments ─── */}
        {isDocumentShareMessage ? (
          <div
            className="chat-attachment-preview"
            style={{
              // align right for sent, left for received — mirrors ChatInput preview strip
              justifyContent: mine ? "flex-end" : "flex-start",
            }}
          >
            {sharedDocuments.map((doc, idx) => (
              <SharedDocCard key={doc.id || idx} doc={doc} mine={mine} />
            ))}
          </div>
        ) : (
          <>
            {/* ── IMAGE ── */}
            {isImage && (
              <div className={`image-message ${mine ? "image-message--sent" : "image-message--received"}`}>
                {msg.content && <div className="image-message__caption">{msg.content}</div>}
                {loadingMedia && (
                  <div className="chat-bubble__text">{t("loading") || "Loading..."}</div>
                )}
                {!loadingMedia && !mediaError && displayUrl && (
                  <a href={displayUrl} target="_blank" rel="noreferrer" className="image-message__link">
                    <img
                      src={displayUrl}
                      alt={msg.fileName || t("messaging.chatMessage.imageAlt") || "image"}
                      className="image-message__img"
                      onError={() => setMediaError(true)}
                    />
                  </a>
                )}
                {!loadingMedia && mediaError && (
                  <div className={`chat-bubble ${mine ? "chat-bubble--sent" : "chat-bubble--received"}`}>
                    <div className="chat-bubble__text">
                      Impossible d'afficher l'image.<br />
                      <a href={originalUrl || "#"} target="_blank" rel="noreferrer">Ouvrir le fichier</a>
                    </div>
                    {process.env.NODE_ENV === "development" && debugInfo && (
                      <pre style={{ marginTop: 8, fontSize: 11, opacity: 0.8, whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(debugInfo, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── VIDEO ── */}
            {isVideo && (
              <div className={`video-message ${mine ? "video-message--sent" : "video-message--received"}`}>
                {msg.content && <div className="video-message__caption">{msg.content}</div>}
                {loadingMedia && (
                  <div className="chat-bubble__text">{t("loading") || "Loading..."}</div>
                )}
                {!loadingMedia && !mediaError && displayUrl && (
                  <video
                    className="video-message__player"
                    controls
                    src={displayUrl}
                    onError={() => setMediaError(true)}
                  />
                )}
                {!loadingMedia && mediaError && (
                  <a href={originalUrl || "#"} target="_blank" rel="noreferrer" className="chat-bubble__file">
                    <FileIcon />
                    <span>{msg.fileName || t("messaging.chatMessage.download") || "Download"}</span>
                  </a>
                )}
              </div>
            )}

            {/* ── TEXT ONLY ── */}
            {!originalUrl && (
              <div className={`chat-bubble ${mine ? "chat-bubble--sent" : "chat-bubble--received"}`}>
                <div className="chat-bubble__text">{msg.content ? t(msg.content) : ""}</div>
              </div>
            )}

            {/* ── NON-MEDIA FILE ── */}
            {originalUrl && !isImage && !isVideo && (
              <div className={`chat-bubble ${mine ? "chat-bubble--sent" : "chat-bubble--received"}`}>
                {msg.content && <div className="chat-bubble__text">{t(msg.content)}</div>}
                <a href={originalUrl} target="_blank" rel="noreferrer" className="chat-bubble__file">
                  <FileIcon />
                  <span>{msg.fileName || t("messaging.chatMessage.file") || "File"}</span>
                </a>
              </div>
            )}
          </>
        )}

        {/* ── TIME ── */}
        <div className={`chat-message-time ${mine ? "chat-message-time--sent" : "chat-message-time--received"}`}>
          {timeStr}
          {mine && msg.isRead !== undefined && (
            <span style={{ marginLeft: 4, color: msg.isRead ? "#34b7f1" : "inherit" }}>
              {msg.isRead ? "✓✓" : "✓"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function FileIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm0 2.5L18.5 9H14z" />
    </svg>
  );
}